/**
 * Module SAV - Liste des dossiers
 * 13/05/2026 - feature/module-sav
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../services/api';
import { Wrench, Plus, Search, FileText } from 'lucide-react';
import SAVCreateModal from './SAVCreateModal';
import SAVDetailPage from './SAVDetailPage';

const STATUTS = [
  { key: '', label: 'Tous' },
  { key: 'RECU', label: 'Recu', color: 'bg-blue-100 text-blue-800' },
  { key: 'EN_COURS', label: 'En cours', color: 'bg-orange-100 text-orange-800' },
  { key: 'TERMINE', label: 'Termine', color: 'bg-green-100 text-green-800' },
  { key: 'REMIS_CLIENT', label: 'Remis client', color: 'bg-gray-100 text-gray-700' }
];
const TYPES = [
  { key: '', label: 'Tous types' },
  { key: 'REPARATION', label: 'Reparation' },
  { key: 'DEVIS', label: 'Devis' },
  { key: 'AUTRE', label: 'Autre' }
];

const statutBadge = (s) => {
  const cfg = STATUTS.find(x => x.key === s);
  return cfg ? cfg : { label: s, color: 'bg-gray-100 text-gray-700' };
};

export default function SAVListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statut, setStatut] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statut) params.set('statut', statut);
      if (type) params.set('type', type);
      if (q) params.set('q', q);
      const res = await fetch(`${API_URL}/sav/dossiers?${params}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur load SAV', e);
    } finally {
      setLoading(false);
    }
  }, [statut, type, q]);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return <SAVDetailPage savId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Wrench className="w-7 h-7 text-blue-600" /> SAV - Service Apres-Vente
        </h1>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nouveau SAV
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-gray-600 block mb-1">Statut</label>
          <select value={statut} onChange={e => setStatut(e.target.value)}
                  className="border rounded px-3 py-2">
            {STATUTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-600 block mb-1">Type</label>
          <select value={type} onChange={e => setType(e.target.value)}
                  className="border rounded px-3 py-2">
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-gray-600 block mb-1">Recherche</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Numero / client / description"
                   className="border rounded pl-8 pr-3 py-2 w-full" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">N SAV</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">Type</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">Article</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">Client</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">Date reception</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">Statut</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600">Prix</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="text-center p-6 text-gray-500">Chargement...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan="7" className="text-center p-6 text-gray-500">Aucun dossier SAV</td></tr>
            )}
            {items.map(s => {
              const badge = statutBadge(s.statut);
              const clientLabel = s.client_nom ||
                (s.client_table_nom ? `${s.client_table_nom} ${s.client_table_prenom || ''}`.trim() : '-');
              return (
                <tr key={s.id} onClick={() => setSelectedId(s.id)}
                    className="border-t hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3 font-mono font-bold text-blue-700">{s.numero_sav}</td>
                  <td className="px-4 py-3 text-sm">{s.type_sav}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium">{s.bijou_nom || '-'}</div>
                    <div className="text-xs text-gray-500 font-mono">
                      {s.reference_article || s.id_produit || ''}
                      {s.is_sav_external ? ' (externe)' : ''}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{clientLabel}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {s.date_reception ? new Date(s.date_reception).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">
                    {s.prix !== null && s.prix !== undefined ? `${parseFloat(s.prix).toLocaleString('fr-FR')} DH` : '-'}
                    {s.tarif_type && s.tarif_type !== 'PAYANT' && (
                      <div className="text-xs text-gray-500">{s.tarif_type}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <SAVCreateModal
          onClose={() => setShowModal(false)}
          onCreated={(id) => { setShowModal(false); load(); setSelectedId(id); }}
        />
      )}
    </div>
  );
}
